import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import resourceService from '../services/resourceService';
import { useAuth } from '../../../core/contexts/AuthContext';

const useResources = (initialFilters = {}) => {
  const { currentUser, userProfile } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [filters, setFilters] = useState({
    subject: 'all',
    resourceType: 'all',
    universityFilter: 'smart', // 'smart', 'my-university', 'all-universities', 'all'
    sortBy: 'createdAt',
    sortOrder: 'desc',
    ...initialFilters
  });
  const [pagination, setPagination] = useState({
    lastDoc: null,
    hasMore: false,
    page: 1
  });
  const [error, setError] = useState(null);

  // Get user's university from profile
  const userUniversity = userProfile?.academic?.university;

  // Fetch resources
  const fetchResources = useCallback(async (resetPagination = true) => {
    try {
      setLoading(true);
      setError(null);

      const lastDoc = resetPagination ? null : pagination.lastDoc;
      const result = await resourceService.fetchResources(
        filters, 
        lastDoc, 
        10, // pageSize
        userUniversity
      );

      if (resetPagination) {
        setResources(result.resources);
        setPagination({
          lastDoc: result.lastDoc,
          hasMore: result.hasMore,
          page: 1
        });
      } else {
        setResources(prev => [...prev, ...result.resources]);
        setPagination(prev => ({
          lastDoc: result.lastDoc,
          hasMore: result.hasMore,
          page: prev.page + 1
        }));
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      setError('Failed to load resources');
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.lastDoc, userUniversity]);

  // Fetch subjects for filtering
  const fetchSubjects = useCallback(async () => {
    try {
      const subjectList = await resourceService.getSubjects();
      setSubjects(subjectList);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  }, []);

  // Fetch universities for filtering
  const fetchUniversities = useCallback(async () => {
    try {
      const universityList = await resourceService.getUniversities();
      setUniversities(universityList);
    } catch (error) {
      console.error('Error fetching universities:', error);
    }
  }, []);

  // Load more resources (pagination)
  const loadMore = useCallback(() => {
    if (pagination.hasMore && !loading) {
      fetchResources(false);
    }
  }, [pagination.hasMore, loading, fetchResources]);

  // Upload a new resource
  const uploadResource = useCallback(async (resourceData, file = null) => {
    if (!currentUser) {
      toast.error('You must be logged in to upload resources');
      return false;
    }

    if (!userProfile?.academic?.university) {
      toast.error('Please complete your university information in your profile first');
      return false;
    }

    try {
      setUploading(true);
      
      const newResource = await resourceService.uploadResource({
        ...resourceData,
        submittedBy: currentUser.uid,
        submittedByUniversity: userProfile.academic.university
      }, file);

      toast.success('Resource uploaded successfully! It will be reviewed before being published.');
      return true;
    } catch (error) {
      console.error('Error uploading resource:', error);
      toast.error('Failed to upload resource');
      return false;
    } finally {
      setUploading(false);
    }
  }, [currentUser, userProfile]);

  // Like a resource
  const likeResource = useCallback(async (resourceId) => {
    if (!currentUser) {
      toast.error('You must be logged in to like resources');
      return;
    }

    try {
      await resourceService.likeResource(resourceId, currentUser.uid);
      
      // Update local state
      setResources(prev => prev.map(resource => {
        if (resource.id === resourceId) {
          const currentInteraction = resource.userInteraction || 'none';
          
          if (currentInteraction === 'liked') {
            return {
              ...resource,
              likes: resource.likes - 1,
              userInteraction: 'none'
            };
          } else if (currentInteraction === 'disliked') {
            return {
              ...resource,
              likes: resource.likes + 1,
              dislikes: resource.dislikes - 1,
              userInteraction: 'liked'
            };
          } else {
            return {
              ...resource,
              likes: resource.likes + 1,
              userInteraction: 'liked'
            };
          }
        }
        return resource;
      }));
    } catch (error) {
      console.error('Error liking resource:', error);
      toast.error('Failed to like resource');
    }
  }, [currentUser]);

  // Dislike a resource
  const dislikeResource = useCallback(async (resourceId) => {
    if (!currentUser) {
      toast.error('You must be logged in to dislike resources');
      return;
    }

    try {
      await resourceService.dislikeResource(resourceId, currentUser.uid);
      
      // Update local state
      setResources(prev => prev.map(resource => {
        if (resource.id === resourceId) {
          const currentInteraction = resource.userInteraction || 'none';
          
          if (currentInteraction === 'disliked') {
            return {
              ...resource,
              dislikes: resource.dislikes - 1,
              userInteraction: 'none'
            };
          } else if (currentInteraction === 'liked') {
            return {
              ...resource,
              likes: resource.likes - 1,
              dislikes: resource.dislikes + 1,
              userInteraction: 'disliked'
            };
          } else {
            return {
              ...resource,
              dislikes: resource.dislikes + 1,
              userInteraction: 'disliked'
            };
          }
        }
        return resource;
      }));
    } catch (error) {
      console.error('Error disliking resource:', error);
      toast.error('Failed to dislike resource');
    }
  }, [currentUser]);

  // Delete a resource
  const deleteResource = useCallback(async (resourceId) => {
    if (!currentUser) {
      toast.error('You must be logged in to delete resources');
      return false;
    }

    try {
      await resourceService.deleteResource(resourceId, currentUser.uid);
      
      // Remove from local state
      setResources(prev => prev.filter(resource => resource.id !== resourceId));
      toast.success('Resource deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast.error('Failed to delete resource');
      return false;
    }
  }, [currentUser]);

  // Increment views when resource is accessed
  const incrementViews = useCallback(async (resourceId) => {
    try {
      await resourceService.incrementViews(resourceId);
      
      // Update local state
      setResources(prev => prev.map(resource => {
        if (resource.id === resourceId) {
          return {
            ...resource,
            views: resource.views + 1
          };
        }
        return resource;
      }));
    } catch (error) {
      console.error('Error incrementing views:', error);
    }
  }, []);

  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters({
      subject: 'all',
      resourceType: 'all',
      universityFilter: 'smart',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  }, []);

  // Get university filter description
  const getUniversityFilterDescription = useCallback(() => {
    switch (filters.universityFilter) {
      case 'my-university':
        return `Showing resources from ${userUniversity} and global resources`;
      case 'all-universities':
        return 'Showing resources available to all universities';
      case 'all':
        return 'Showing all resources';
      case 'smart':
      default:
        return userUniversity 
          ? `Showing relevant resources for ${userUniversity}`
          : 'Showing all resources';
    }
  }, [filters.universityFilter, userUniversity]);

  // Initial load
  useEffect(() => {
    fetchResources(true);
  }, [filters]);

  // Load subjects and universities on mount
  useEffect(() => {
    fetchSubjects();
    fetchUniversities();
  }, [fetchSubjects, fetchUniversities]);

  return {
    resources,
    loading,
    uploading,
    subjects,
    universities,
    filters,
    pagination,
    error,
    userUniversity,
    fetchResources,
    loadMore,
    uploadResource,
    likeResource,
    dislikeResource,
    deleteResource,
    incrementViews,
    updateFilters,
    resetFilters,
    getUniversityFilterDescription
  };
};

export default useResources;